import type { ComposerTranslation } from 'vue-i18n'
import { IS_PRIVATE } from '@/common/const/env'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import type { RegularDeploymentItem } from './client-alive.service'

export interface DeployDownloadMenuItem {
  label: string
  url: string
}

/** 与终端部署页左侧 OS + Linux Tab 一致的分组下拉 */
export interface DeployDownloadMenuSection {
  title: string
  items: DeployDownloadMenuItem[]
}

const LINUX_TONGXIN_PREFIX = '统信系统'
const LINUX_QILIN_PREFIX = '麒麟系统'
const LINUX_CENTOS_PREFIX = 'CentOS'
const LINUX_UBUNTU_PREFIX = 'Ubuntu'
const LINUX_ARCH_ORDER = ['amd64', 'arm64', 'mips64el', 'mips64', 'loongarch64']

/** 大小写不敏感的 startsWith */
function startsWithIgnoreCase (str: string, prefix: string): boolean {
  return str.length >= prefix.length &&
    str.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()
}

interface LinuxDistroTabEntry {
  tabId: string
  /** 与终端部署页 Linux 顶栏 Tab 文案一致 */
  label: string
  mapKeys: string[]
}

/** 展示用：去掉 mapKey 末尾架构段（与 client-alive.vue 一致） */
function linuxMapKeyDisplayBase (mapKey: string): string {
  const trimmed = mapKey.trim()
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length < 2) {
    return trimmed
  }
  const last = parts[parts.length - 1].toLowerCase()
  const archSet = new Set(LINUX_ARCH_ORDER.map(a => a.toLowerCase()))
  if (archSet.has(last)) {
    return parts.slice(0, -1).join(' ')
  }
  return trimmed
}

function linuxMapKeyArchSuffix (mapKey: string): string {
  const parts = mapKey.trim().split(/\s+/)
  return parts[parts.length - 1] || mapKey
}

function sortLinuxGroupMapKeys (mapKeys: string[]): string[] {
  return [...mapKeys].sort((a, b) => {
    const archA = linuxMapKeyArchSuffix(a)
    const archB = linuxMapKeyArchSuffix(b)
    const ia = LINUX_ARCH_ORDER.indexOf(archA)
    const ib = LINUX_ARCH_ORDER.indexOf(archB)
    if (ia !== -1 && ib !== -1) return ia - ib
    if (ia !== -1) return -1
    if (ib !== -1) return 1
    return a.localeCompare(b, 'zh-CN')
  })
}

function buildLinuxDistroTabEntries (systems: string[]): LinuxDistroTabEntry[] {
  const tongxin = systems.filter(s => s.startsWith(LINUX_TONGXIN_PREFIX))
  const qilin = systems.filter(s => s.startsWith(LINUX_QILIN_PREFIX))
  const centos = systems.filter(s => startsWithIgnoreCase(s, LINUX_CENTOS_PREFIX))
  const ubuntu = systems.filter(s => startsWithIgnoreCase(s, LINUX_UBUNTU_PREFIX))
  const rest = systems.filter(
    s => !s.startsWith(LINUX_TONGXIN_PREFIX) &&
      !s.startsWith(LINUX_QILIN_PREFIX) &&
      !startsWithIgnoreCase(s, LINUX_CENTOS_PREFIX) &&
      !startsWithIgnoreCase(s, LINUX_UBUNTU_PREFIX)
  )
  const entries: LinuxDistroTabEntry[] = rest.map(k => ({
    tabId: k,
    label: linuxMapKeyDisplayBase(k),
    mapKeys: [k],
  }))
  if (centos.length) {
    entries.push({
      tabId: '__linux_centos__',
      label: LINUX_CENTOS_PREFIX,
      mapKeys: sortLinuxGroupMapKeys(centos),
    })
  }
  if (ubuntu.length) {
    entries.push({
      tabId: '__linux_ubuntu__',
      label: LINUX_UBUNTU_PREFIX,
      mapKeys: sortLinuxGroupMapKeys(ubuntu),
    })
  }
  if (tongxin.length) {
    entries.push({
      tabId: '__linux_tongxin__',
      label: LINUX_TONGXIN_PREFIX,
      mapKeys: sortLinuxGroupMapKeys(tongxin),
    })
  }
  if (qilin.length) {
    entries.push({
      tabId: '__linux_qilin__',
      label: LINUX_QILIN_PREFIX,
      mapKeys: sortLinuxGroupMapKeys(qilin),
    })
  }
  return entries
}

function linuxTabItems (
  tab: LinuxDistroTabEntry,
  map: Record<string, unknown>,
  t: ComposerTranslation
): DeployDownloadMenuItem[] {
  const list: DeployDownloadMenuItem[] = []
  const pkgWord = t('terminalDeploy.installPackage')
  for (const system of tab.mapKeys) {
    const val = map[system]
    if (typeof val === 'string' && val) {
      list.push({
        label: `${system} ${pkgWord}`.trim(),
        url: val,
      })
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, string>
      for (const [arch, url] of Object.entries(obj)) {
        if (url) {
          list.push({
            label: `${system} (${arch}) ${pkgWord}`.trim(),
            url,
          })
        }
      }
    }
  }
  return list
}

export type DeployOsNavKey = 'windows' | 'win-server' | 'macos' | 'linux'

export interface LinuxDistroDownloadRow {
  tabId: string
  label: string
  items: DeployDownloadMenuItem[]
}

export function getWindowsPackages (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): DeployDownloadMenuItem[] {
  const winItems: DeployDownloadMenuItem[] = []
  if (data.windows_download_url) {
    winItems.push({ label: t('terminalDeploy.windowsPackage'), url: data.windows_download_url })
  }
  if (!IS_JP_ONLY_BUILD && data.windows_xp_download_url) {
    winItems.push({ label: t('terminalDeploy.windowsXPPackage'), url: data.windows_xp_download_url })
  }
  return winItems
}

export function getWinServerPackages (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): DeployDownloadMenuItem[] {
  const serverItems: DeployDownloadMenuItem[] = []
  if (data.win_server_download_url) {
    serverItems.push({ label: t('terminalDeploy.windowsServerPackage'), url: data.win_server_download_url })
  }
  if (!IS_JP_ONLY_BUILD && data.windows_server2003_download_url) {
    serverItems.push({
      label: t('terminalDeploy.windowsServer2003Package'),
      url: data.windows_server2003_download_url,
    })
  }
  return serverItems
}

export function getMacosPackages (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): DeployDownloadMenuItem[] {
  const macItems: DeployDownloadMenuItem[] = []
  if (!IS_PRIVATE && !HIDE_FUNC.macos) {
    if (data.macos_arm64_download_url) {
      macItems.push({ label: t('terminalDeploy.appleChipPackage'), url: data.macos_arm64_download_url })
    }
    if (data.macos_amd64_download_url) {
      macItems.push({ label: t('terminalDeploy.intelChipPackage'), url: data.macos_amd64_download_url })
    }
  }
  return macItems
}

/** Linux 发行版 Tab + 包列表（与终端部署页顶栏 Tab 一致） */
export function getLinuxDistroDownloadRows (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): LinuxDistroDownloadRow[] {
  const map = data.linux_download_map || {}
  const systemsRaw = (data.systems?.length ? data.systems : Object.keys(map)) as string[]
  if (IS_PRIVATE && Object.keys(map).length === 0) {
    return []
  }
  const tabEntries = buildLinuxDistroTabEntries(systemsRaw)
  return tabEntries
    .map(tab => ({
      tabId: tab.tabId,
      label: tab.label,
      items: linuxTabItems(tab, map, t),
    }))
    .filter(row => row.items.length > 0)
}

/**
 * 与终端部署页一致：Windows / Windows Server / macOS / Linux（Linux 按发行版 Tab 分子组）
 */
export function buildCategorizedDeployDownloadSections (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): DeployDownloadMenuSection[] {
  const sections: DeployDownloadMenuSection[] = []

  const winItems = getWindowsPackages(data, t)
  if (winItems.length) {
    sections.push({ title: t('terminalDeploy.osNavWindows'), items: winItems })
  }

  const serverItems = getWinServerPackages(data, t)
  if (serverItems.length) {
    sections.push({ title: t('terminalDeploy.osNavWinServer'), items: serverItems })
  }

  const macItems = getMacosPackages(data, t)
  if (macItems.length) {
    sections.push({ title: t('terminalDeploy.osNavMacOS'), items: macItems })
  }

  const linuxBase = t('terminalDeploy.osNavLinux')
  const nonEmptyTabs = getLinuxDistroDownloadRows(data, t)
  for (const row of nonEmptyTabs) {
    const title = nonEmptyTabs.length === 1 ? linuxBase : `${linuxBase} · ${row.label}`
    sections.push({ title, items: row.items })
  }

  return sections
}

/**
 * 扁平列表（分类结果的顺序拼接，兼容旧用法）
 */
export function buildFlatDeployDownloadMenuEntries (
  data: RegularDeploymentItem,
  t: ComposerTranslation
): DeployDownloadMenuItem[] {
  return buildCategorizedDeployDownloadSections(data, t).flatMap(s => s.items)
}
